import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { motionAllowed } from "@/motion/MotionProvider";
import { useTiltCard, useRevealAnimation, useTextReveal } from "@/motion/hooks";
import { Play, X, VolumeX, Volume2 } from "lucide-react";
import { CATEGORIES, GALLERY } from "@/data/gallery";

function Tile({ item, onOpen }) {
    const tiltRef = useTiltCard(4);
    const imgReveal = useRevealAnimation("maskReveal");
    const [imgOk, setImgOk] = useState(true);
    return (
        <button
            type="button"
            data-testid={`gallery-item-${item.id}`}
            onClick={() => onOpen(item)}
            ref={tiltRef}
            className="group relative block w-full overflow-hidden border border-[color:var(--line)] bg-[color:var(--bg-elev)] text-left will-change-transform"
        >
            <div className="relative aspect-[4/5] w-full">
                {imgOk ? (
                    <img
                        ref={imgReveal}
                        src={item.poster}
                        alt={item.alt}
                        loading="lazy"
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                        onError={() => item.type !== "youtube" && setImgOk(false)}
                        className="h-full w-full object-cover opacity-80 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100"
                    />
                ) : (
                    // Graceful upload slot until the real thumbnail is added.
                    <div className="flex h-full w-full flex-col items-center justify-center gap-3 p-6 text-center">
                        <span className="font-display text-2xl text-[color:var(--gold)]/70">
                            {item.categoryName}
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)]">
                            Upload slot
                        </span>
                        <code className="text-[10px] text-[color:var(--muted)]/80 break-all">
                            {item.poster}
                        </code>
                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                {item.type !== "image" && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[color:var(--gold)]/60 bg-black/40 backdrop-blur-md opacity-0 transition-opacity group-hover:opacity-100">
                            <Play size={16} className="text-[color:var(--gold)]" />
                        </span>
                    </div>
                )}
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 transition-opacity group-hover:opacity-100">
                <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--fg)]/85 line-clamp-1">
                    {item.caption}
                </p>
            </div>
        </button>
    );
}

function Lightbox({ item, onClose }) {
    const boxRef = useRef(null);
    useEffect(() => {
        if (!boxRef.current || !motionAllowed()) return;
        gsap.fromTo(boxRef.current, { opacity: 0, scale: 0.985 },
            { opacity: 1, scale: 1, duration: 0.3, ease: "luxe" });
    }, []);
    const [muted, setMuted] = useState(true);
    const [videoOk, setVideoOk] = useState(true);

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [onClose]);

    return (
        <div
            ref={boxRef}
            data-testid="gallery-lightbox"
            onClick={onClose}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 md:p-10"
        >
            <button
                type="button"
                data-testid="lightbox-close"
                onClick={onClose}
                className="absolute right-5 top-5 z-10 flex h-10 w-10 items-center justify-center border border-[color:var(--line)] text-[color:var(--fg)] hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] transition-colors"
                aria-label="Close"
            >
                <X size={18} />
            </button>

            <div
                onClick={(e) => e.stopPropagation()}
                onContextMenu={(e) => e.preventDefault()}
                className="relative w-full max-w-4xl"
            >
                {item.type === "youtube" ? (
                    <div className="aspect-video w-full bg-black">
                        <iframe
                            key={item.youtubeId}
                            data-testid="lightbox-youtube"
                            src={`https://www.youtube-nocookie.com/embed/${item.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                            title={item.caption}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="h-full w-full"
                        />
                    </div>
                ) : videoOk ? (
                    <video
                        key={item.src}
                        src={item.src}
                        poster={item.poster}
                        autoPlay
                        loop
                        muted={muted}
                        playsInline
                        controls
                        controlsList="nodownload noremoteplayback"
                        disablePictureInPicture
                        onContextMenu={(e) => e.preventDefault()}
                        onError={() => setVideoOk(false)}
                        className="max-h-[80vh] w-full bg-black object-contain"
                    />
                ) : (
                    <div className="flex aspect-video w-full flex-col items-center justify-center gap-4 border border-[color:var(--line)] bg-[color:var(--bg-elev)] p-10 text-center">
                        <span className="font-display text-3xl text-[color:var(--gold)]/80">
                            {item.categoryName}
                        </span>
                        <p className="text-sm text-[color:var(--muted)]">
                            No media uploaded yet for this slot.
                        </p>
                        <code className="text-xs text-[color:var(--muted)]/80 break-all">
                            {item.src}
                        </code>
                    </div>
                )}

                <div className="mt-4 flex items-center justify-between gap-4">
                    <p className="text-sm text-[color:var(--fg)]/80">{item.caption}</p>
                    {videoOk && item.type !== "youtube" && (
                        <button
                            type="button"
                            data-testid="lightbox-mute"
                            onClick={() => setMuted((m) => !m)}
                            className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[color:var(--muted)] hover:text-[color:var(--gold)] transition-colors"
                        >
                            {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
                            {muted ? "Sound off" : "Sound on"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function Gallery() {
    const headRef = useRevealAnimation("cinematic", { children: "[data-gallery-head]", stagger: "tight" });
    const h2Reveal = useTextReveal("chars");
    const gridRef = useRevealAnimation("fadeBlur", { children: "[data-testid^=gallery-item-]", stagger: 0.06 });
    const [active, setActive] = useState("all");
    const [selected, setSelected] = useState(null);

    const items = active === "all" ? GALLERY : GALLERY.filter((i) => i.category === active);
    const tabs = [{ slug: "all", name: "All" }, ...CATEGORIES];

    return (
        <section
            id="work"
            data-testid="work-section"
            className="relative border-b border-[color:var(--line)] py-24 md:py-36"
        >
            <div className="mx-auto max-w-[1400px] px-6 md:px-10">
                <div className="text-center">
                    <p className="text-[10px] uppercase tracking-luxe text-[color:var(--gold)]">
                        Selected Work
                    </p>
                    <h2 ref={h2Reveal} className="font-display mt-4 text-5xl md:text-6xl">The Work</h2>
                </div>

                {/* Category filter tabs */}
                <div className="no-scrollbar mt-12 flex gap-2 overflow-x-auto pb-2 md:flex-wrap md:justify-center">
                    {tabs.map((t) => (
                        <button
                            key={t.slug}
                            type="button"
                            data-testid={`filter-${t.slug}`}
                            onClick={() => setActive(t.slug)}
                            className={`whitespace-nowrap border px-4 py-2 text-[11px] uppercase tracking-[0.16em] transition-all duration-[250ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] hover:scale-[1.04] hover:border-[color:var(--gold-bright)] hover:shadow-[0_0_18px_rgba(198,167,107,0.25)] active:scale-[0.98] will-change-transform ${
                                active === t.slug
                                    ? "border-[color:var(--gold)] bg-[color:var(--gold)] text-black"
                                    : "border-[color:var(--line)] text-[color:var(--fg)]/70 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)]"
                            }`}
                        >
                            {t.name}
                        </button>
                    ))}
                </div>

                {/* Masonry gallery */}
                <div className="masonry mt-12 columns-2 md:columns-3 lg:columns-4">
                    {items.map((item) => (
                        <Tile key={item.id} item={item} onOpen={setSelected} />
                    ))}
                </div>
            </div>

                {selected && <Lightbox item={selected} onClose={() => setSelected(null)} />}
        </section>
    );
}
